import React, { useState, useEffect, useCallback } from 'react';
import TrashIcon from '@/assets/trash-light.svg';
import { Color, Palette, ColorFilter, FilterState, SortOption, ColorLibraryData } from '../../types/ColorLibraryModel';
import { sortByGrayScale, colorFromBgColor, isColorDark } from '../../utils/color-library-utils';
import { loadColorLibrary } from '../../api/color-library';
import ColorSquare from './ColorSquare';
import ColorDetails from './ColorDetails';
import ColorDetailsEmpty from './ColorDetailsEmpty';
import ColorFilters from './ColorFilters';
import Modal from '../general/Modal';
import Dropdown from '../formControl/Dropdown';

export interface ColorLibraryAllProps {
  data: ColorLibraryData & {
    colours?: Color[];
    effects?: string[];
    seasons?: any[];
  };
  onUpdatePalette: (palette: Palette) => void;
  onDownloadColor: (colors: Color[]) => void;
  isActive?: boolean;
}

type SeasonKey = 'A/W' | 'S/S';

function parseLabel(label: string): { year: number; season: SeasonKey } {
  const season: SeasonKey = label.startsWith('A/W') ? 'A/W' : 'S/S';
  const year = Number.parseInt(label.match(/\d+/)?.[0] ?? '0', 10);
  return { year, season };
}

const ColorLibraryAll: React.FC<ColorLibraryAllProps> = ({
  data = { backgroundColor: '#ffffff', palettes: [] },
  onUpdatePalette,
  onDownloadColor,
  isActive = true,
}) => {
  const [mobileFiltersPanel, setMobileFiltersPanel] = useState(0);
  const [colors, setColors] = useState<Color[]>([]);

  const [filters, setFilters] = useState<ColorFilter[]>([
    { label: 'Colour', count: 0, types: [] },
    { label: 'Effect', count: 0, types: [] },
    { label: 'Season', count: 0, types: [] },
  ]);
  const [sorting] = useState<SortOption[]>([
    { label: 'Dark to Light', code: 'darktoLight' },
    { label: 'Light to Dark', code: 'lighttoDark' },
  ]);
  const [selectedColors, setSelectedColors] = useState<Color[]>([]);
  const [activeSorting, setActiveSorting] = useState<SortOption>(sorting[0]);
  const [selectedColor, setSelectedColor] = useState<Color | null>(null);
  const [allColors, setAllColors] = useState<Color[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1024);

  const sortByYearDesc = (
    arr: { categoryCodeName: string; categoryDisplayName: string }[],
  ): { categoryCodeName: string; categoryDisplayName: string }[] => {
    return arr.sort((a, b) => {
      const A = parseLabel(a.categoryDisplayName);
      const B = parseLabel(b.categoryDisplayName);

      // primary: year descending
      if (A.year !== B.year) return B.year - A.year;

      // tie-breaker: A/W before S/S
      if (A.season !== B.season) return A.season === 'A/W' ? -1 : 1;

      // final tie-breaker: consistent ordering
      return a.categoryDisplayName.localeCompare(b.categoryDisplayName);
    });
  };

  const loadColors = useCallback(async () => {
    let colours = data?.colours || [];

    // Load from API if no data provided
    if (colours.length === 0) {
      try {
        const apiData = await loadColorLibrary();
        colours = apiData.colours || [];
      } catch (error) {
        console.error('Error loading colors from API:', error);
        return;
      }
    }

    if (colours.length === 0) {
      console.warn('No colors loaded, check API response');
      return;
    }

    // Extract dropdown options from API data
    const effects = [...new Set(colours.map((c) => c.effect).filter(Boolean))];
    const seasonsMap = new Map();
    colours.forEach((c) => {
      c.seasons?.forEach((s) => {
        if (!seasonsMap.has(s.categoryCodeName)) {
          seasonsMap.set(s.categoryCodeName, {
            categoryCodeName: s.categoryCodeName,
            categoryDisplayName: s.categoryDisplayName,
          });
        }
      });
    });
    const seasons = sortByYearDesc(Array.from(seasonsMap.values()));

    setColors(colours);

    // Fill filters from API data
    setFilters([
      { label: 'Colour', count: 0, types: [...new Set(colours.map((c) => c.group).filter(Boolean))] },
      { label: 'Effect', count: 0, types: effects },
      { label: 'Season', count: 0, types: seasons },
    ]);

    // Process colors with HSP values and sort
    const sortedColors = sortByGrayScale(colours, { code: 'darktoLight' });
    setAllColors(sortedColors);
    setLoaded(true);
  }, [data?.colours]);

  useEffect(() => {
    if (!loaded) {
      loadColors();
    }
  }, [loaded, loadColors]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const handleToggleColor = useCallback(
    (color: Color) => {
      setSelectedColors((prev) => {
        const index = prev.findIndex((c) => c.id === color.id);
        if (index === -1) {
          setSelectedColor(color);
          return [...prev, color];
        } else {
          const newSelected = prev.filter((c) => c.id !== color.id);
          if (color.id === selectedColor?.id) {
            setSelectedColor(newSelected[0] || null);
          }
          return newSelected;
        }
      });
    },
    [selectedColor?.id],
  );

  const handleSelectColor = useCallback(
    (color: Color) => {
      setSelectedColor(color);
      if (windowWidth < 768) {
        setShowColorModal(true);
      }
    },
    [windowWidth],
  );

  const handleDeleteColor = useCallback(
    (color: Color) => {
      setSelectedColors((prev) => {
        const newSelected = prev.filter((c) => c.id !== color.id);
        if (color.id === selectedColor?.id) {
          setSelectedColor(newSelected[0] || null);
        }
        return newSelected;
      });
    },
    [selectedColor?.id],
  );

  const handleSortChange = useCallback(
    (option: SortOption) => {
      setActiveSorting(option);
      const sorted = sortByGrayScale(colors, option);
      setAllColors(sorted);
    },
    [colors],
  );

  const handleFiltering = useCallback(
    (filterState: FilterState | null) => {
      let filteredColors = colors;

      if (filterState === null) {
        // Reset filters
        setSelectedColor(null);
        setSelectedColors([]);
        const sorted = sortByGrayScale(filteredColors, activeSorting);
        setAllColors(sorted);
        return;
      }

      // Apply filters only if they have values
      filteredColors = filteredColors.filter((color) => {
        const effectMatch = filterState.effectFilter.length === 0 || filterState.effectFilter.includes(color.effect);

        const groupMatch =
          filterState.colourGroupFilter.length === 0 || filterState.colourGroupFilter.includes(color.group);

        const seasonMatch =
          filterState.seasonFilter.length === 0 ||
          (color.seasons && color.seasons.some((season) => filterState.seasonFilter.includes(season.categoryCodeName)));

        return effectMatch && groupMatch && seasonMatch;
      });

      const sorted = sortByGrayScale(filteredColors, activeSorting);
      setAllColors(sorted);

      // Update selected colors based on filtered results
      setSelectedColors((prevSelectedColors) => {
        const newSelectedColors = prevSelectedColors.filter((selected) =>
          sorted.some((filtered) => filtered.id === selected.id),
        );

        if (sorted.length === 1) {
          setSelectedColor(sorted[0]);
        } else if (sorted.length === 0) {
          setSelectedColor(null);
        } else {
          setSelectedColor((prevSelectedColor) =>
            prevSelectedColor && !sorted.some((c) => c.id === prevSelectedColor.id)
              ? newSelectedColors[0] || null
              : prevSelectedColor,
          );
        }

        return newSelectedColors;
      });
    },
    [colors, activeSorting],
  );

  const handleSelectAllColors = useCallback(() => {
    setSelectedColors([...allColors]);
    setSelectedColor(allColors[0] || null);
  }, [allColors]);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  return (
    <div>
      {/* Filters */}
      {isActive && (
        <div className="mb-4 md:mb-6">
          {isMobile ? (
            <>
              <button
                onClick={() => setMobileFiltersPanel(mobileFiltersPanel === 1 ? 0 : 1)}
                className="mt-3 h-11 cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition-colors hover:bg-gray-50"
                style={{ borderColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
              >
                Filters
              </button>

              {mobileFiltersPanel === 1 && (
                <div
                  className="mt-3 rounded-2xl border p-4"
                  style={{ borderColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
                >
                  <div className="mb-4 text-center">
                    <div
                      className="flex h-11 w-full items-center rounded-full border px-4 py-2"
                      style={{ borderColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
                    >
                      <div className="flex w-full items-center justify-center hover:bg-gray-300">
                        <div className="flex-shrink-0 px-2 text-center">
                          <span className="text-sm font-semibold">Sort</span>
                        </div>
                        <div
                          className="mx-2 h-4 w-px"
                          style={{ backgroundColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
                        />
                        <div className="flex-1">
                          <Dropdown
                            name="sort"
                            options={sorting.map((s) => ({ label: s.label, value: s.code }))}
                            value={activeSorting.code}
                            onChange={(e) => {
                              const option = sorting.find((s) => s.code === e.target.value);
                              if (option) handleSortChange(option);
                            }}
                            className="[&_.main-button]:h-auto [&_.main-button]:border-none [&_.main-button]:bg-transparent [&_.main-button]:p-0 [&_.main-button]:text-sm [&_.main-button]:font-semibold [&_.main-button]:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <ColorFilters
                    filters={filters}
                    colors={selectedColors}
                    onFilterChange={handleFiltering}
                    onSelectAllColors={handleSelectAllColors}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="grid grid-cols-12 gap-3 lg:gap-4">
              <div className={`${isTablet ? 'col-span-7' : 'col-span-8 lg:col-span-9'}`}>
                <ColorFilters
                  filters={filters}
                  colors={selectedColors}
                  onFilterChange={handleFiltering}
                  onSelectAllColors={handleSelectAllColors}
                />
              </div>
              <div className={`${isTablet ? 'col-span-5' : 'col-span-4 lg:col-span-3'} flex justify-end`}>
                <div
                  className="flex h-11 max-w-xs cursor-pointer items-center rounded-full border px-4 py-2 transition-colors hover:bg-gray-100"
                  style={{ borderColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
                >
                  <div className="flex items-center justify-center">
                    <div className="px-2 text-center">
                      <span className="text-sm font-medium lg:text-base">Sort</span>
                    </div>
                    <div
                      className="mx-2 h-4 w-px"
                      style={{ backgroundColor: colorFromBgColor(data?.backgroundColor || '#ffffff', 0.2) }}
                    />
                    <div className="flex-1">
                      <Dropdown
                        name="sort"
                        options={sorting.map((s) => ({ label: s.label, value: s.code }))}
                        value={activeSorting.code}
                        onChange={(e) => {
                          const option = sorting.find((s) => s.code === e.target.value);
                          if (option) handleSortChange(option);
                        }}
                        className="[&_.main-button]:h-auto [&_.main-button]:border-none [&_.main-button]:bg-transparent [&_.main-button]:p-0 [&_.main-button]:text-sm [&_.main-button]:font-medium [&_.main-button]:outline-none lg:[&_.main-button]:text-base"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Layout */}
      <div className="block md:hidden">
        <div className="mb-4">
          <h3 className="mb-3 text-base font-medium sm:text-lg">Selected Colors:</h3>
          {selectedColors.length > 0 ? (
            <div className="space-y-2">
              {selectedColors.map((color) => (
                <div
                  key={color.id}
                  className="block rounded-lg px-3 py-2 text-sm transition-all duration-200"
                  style={{
                    backgroundColor: `#${color.hexCode}`,
                    color: isColorDark(color.hexCode) ? 'white' : 'black',
                  }}
                >
                  {color.displayName}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No colors selected</p>
          )}
        </div>
        <div className="mt-8 flex w-full flex-wrap">
          {allColors.map((color) => (
            <ColorSquare
              key={color.id}
              color={color}
              selectedColors={selectedColors}
              palettes={data.palettes}
              onClick={handleSelectColor}
              onAddColorToPalette={onUpdatePalette}
              onDownloadColor={onDownloadColor}
            />
          ))}
        </div>

        {/* Mobile Color Details Modal */}
        <Modal
          open={showColorModal}
          onClose={() => setShowColorModal(false)}
          contanerClassName="max-w-sm mx-4 rounded-xl overflow-hidden shadow-2xl"
        >
          {selectedColor && (
            <div className="relative">
              <button
                onClick={() => setShowColorModal(false)}
                className="absolute top-4 right-4 z-10 cursor-pointer text-2xl leading-none transition-all duration-200 hover:scale-110 hover:opacity-70"
                style={{ color: isColorDark(selectedColor.hexCode) ? 'white' : 'black' }}
              >
                ×
              </button>
              <ColorDetails
                color={selectedColor}
                colors={selectedColors}
                palettes={data.palettes}
                onAddColorToPalette={onUpdatePalette}
                onDownloadColor={onDownloadColor}
                showSaveToScrapbookButton={false}
              />
            </div>
          )}
        </Modal>
      </div>

      {/* Desktop Layout */}
      <div className="relative mx-0 hidden md:block">
        <div className="flex gap-6">
          <div className="w-7/12 md:w-8/12 lg:w-9/12">
            <div className="flex flex-wrap">
              {allColors.map((color) => (
                <ColorSquare
                  key={color.id}
                  color={color}
                  selectedColors={selectedColors}
                  selectedColor={selectedColor}
                  palettes={data.palettes}
                  onToggleColor={handleToggleColor}
                  onAddColorToPalette={onUpdatePalette}
                  onDownloadColor={onDownloadColor}
                />
              ))}
            </div>
          </div>
          <div className="w-5/12 md:w-4/12 lg:w-3/12">
            {selectedColors.length > 1 && (
              <div className="mb-4">
                <h3 className="text-lg font-medium lg:text-xl">Selected {selectedColors.length} Colours</h3>
              </div>
            )}
            <div className="">
              {selectedColors.slice(0, 9).map(
                (colorItem) =>
                  selectedColor?.id !== colorItem.id && (
                    <div
                      key={colorItem.id}
                      className="flex cursor-pointer items-center p-2 transition-all duration-200 hover:shadow-md"
                      style={{ backgroundColor: `#${colorItem.hexCode}` }}
                      onClick={() => handleSelectColor(colorItem)}
                    >
                      <h3
                        className="ml-3 flex-1 text-sm font-medium lg:text-base"
                        style={{ color: isColorDark(colorItem.hexCode) ? 'white' : 'black' }}
                      >
                        {colorItem.displayName}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteColor(colorItem);
                        }}
                        className="cursor-pointer p-1 transition-all duration-200 hover:scale-110"
                        title="Remove from selection"
                      >
                        <TrashIcon
                          className={`h-5 w-5 lg:h-6 lg:w-6 ${isColorDark(colorItem.hexCode) ? 'brightness-0 contrast-100 invert' : ''}`}
                        />
                      </button>
                    </div>
                  ),
              )}
            </div>
            <div className="sticky top-[10%]">
              {selectedColor ? (
                <ColorDetails
                  color={selectedColor}
                  colors={selectedColors}
                  palettes={data.palettes}
                  onAddColorToPalette={onUpdatePalette}
                  onDownloadColor={onDownloadColor}
                />
              ) : (
                <ColorDetailsEmpty data={data} color={selectedColor} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorLibraryAll;
